import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/Auth';
import Datetime from 'react-datetime';
import add from 'date-fns/add';
import "react-datetime/css/react-datetime.css";
import { Grid } from "react-loader-spinner";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useTranslation } from 'react-i18next';

export default function AuctionForm({ history, match }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const chitId = match.params.chitId;
  const auctionId = match.params.auctionId;
  const isAddMode = !auctionId;

  // form validation rules
  const validationSchema = yup.object().shape({
    auctionDate: yup.date(),
    fee: yup.number().notRequired(),
    expense: yup.number().notRequired(),
    auctionAmount: yup.number().notRequired().positive(),
    firstAuctionMembersPay: yup.number().notRequired().positive()
  });

  // functions to build form returned by useForm() hook
  const { control, register, formState: { errors, isSubmitting }, handleSubmit, reset, setValue, getValues } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const [chit, setChit] = useState({});
  const [currentAuctionCount, setCurrentAuctionCount] = useState(0);
  const [prevAuction, setPrevAuction] = useState({});
  const [hasPrevAuction, setHasPrevAuction] = useState(false);
  const [loadingChit, setLoadingChit] = useState(true);
  const [loadingPrevAuction, setLoadingPrevAuction] = useState(false);
  const [loadingAuction, setLoadingAuction] = useState(false);
  const [addingAuction, setAddingAuction] = useState(false);
  const [addAuctionError, setAddAuctionError] = useState("");
  const [missingPayableCalcError, setMissingPayableCalcError] = useState("");
  const [auctionAmountMode, setAuctionAmountMode] = useState(true);
  const [hasWonAuction, setHasWonAuction] = useState(false);

  const [payableCalculation, setPayableCalculation] = useState({
    previousWinnersPay: 0.0,
    previousWinnersCount: 0,
    commission: 0.0,
    totalExpense: 0.0,
    remainingMembers: 0,
    remainingMembersPay: 0.0,
    winnerTakes: 0.0
  });
  const [payableCalculated, setPayableCalculated] = useState(false);

  // Fetch all the pre-requisites for the page
  // 1. Chit - to show chitName in heading
  // 2. Previous Auction - to show prev. auctionAmount, fee & expense
  // 3. Auction - in Edit view
  useEffect(() => {
    setValue('amountChoiceSelection', auctionAmountMode);
    setValue('hasWonSelection', hasWonAuction);
  
    const fetchChit = async (id) => {
      let { data: chit, error } = await supabase
        .from("chits")
        .select("*")
        .eq('id', id)
        .single();
      if (error) {
        // TODO: Show error to user instead of suppressing
        setLoadingChit(false);
        console.log("Error while fetching chit", error);
      } else {
        setChit(chit);
        if (isAddMode) {
          const currentInstallmentCount = chit.next_installment_count;
          setValue('auctionDate', new Date(chit['next_auction_date']));
          setCurrentAuctionCount(currentInstallmentCount);
          setValue('expense', 0);
          setValue('fee', 0);
          if (currentInstallmentCount > 1) {
            setLoadingPrevAuction(true);
            fetchPrevAuction(chitId, currentInstallmentCount)
              .catch(console.error);
          }
        }
        setLoadingChit(false);
      }
    };

    const fetchPrevAuction = async (chitId, auctionNumber) => {
      const prevAuctionNumber = auctionNumber - 1;
      let { data: prevAuction, prevAuctionFetchError } = await supabase
        .from("auctions")
        .select("*")
        .match({ chit_id: chitId, auction_number: prevAuctionNumber })
        .single();
      if (prevAuctionFetchError) {
        setLoadingPrevAuction(false);
        console.log("Error while fetching previous auction", prevAuctionFetchError);
      } else {
        if (isAddMode) {
          setValue('expense', prevAuction['expense'] ? Number(prevAuction['expense']) : 0);
          setValue('fee', prevAuction['fee'] ? Number(prevAuction['fee']) : 0);
        }
        setPrevAuction(prevAuction);
        setHasPrevAuction(true);
        setLoadingPrevAuction(false);
      }
    }

    const fetchAuction = async (id) => {
      let { data: auction, auctionFetchError } = await supabase
        .from("auctions")
        .select("*")
        .eq('id', id)
        .single();
      if (auctionFetchError) {
        // TODO: Show error to user instead of suppressing
        console.log("No auction found");
        console.log("error", auctionFetchError);
        setLoadingAuction(false);
      } else {
        const currentInstallmentCount = auction['auction_number'];
        setCurrentAuctionCount(currentInstallmentCount);
        if (currentInstallmentCount === 1) {
          setValue('firstAuctionMembersPay', auction['rem_members_pay']);
        }
        const fields = {
          'auctionAmount': 'auction_amount',
          'auctionDate': 'auction_date',
          'expense': 'expense',
          'fee': 'fee',
          'winnerName': 'winner_name',
          'hasWon': 'has_won'
        };
        for (const field in fields) {
          if (field === 'auctionDate') {
            setValue(field, new Date(auction[fields[field]]));
          } else if (field === 'winnerName' || field === 'hasWon') {
            setValue(field, auction[fields[field]]);
          } else {
            setValue(field, auction[fields[field]] ? Number(auction[fields[field]]) : 0);
          }
        }
        setHasWonAuction(auction['has_won']);
        if (currentInstallmentCount > 1) {
          setLoadingPrevAuction(true);
          fetchPrevAuction(chitId, currentInstallmentCount)
            .catch(console.error);
        }
        setLoadingAuction(false);
      }
    }

    fetchChit(chitId).catch(console.error);
    if (!isAddMode) {
      // get the Auction data and set form fields
      setLoadingAuction(true);
      fetchAuction(auctionId);
    }
  }, [isAddMode, chitId, auctionId, setValue, auctionAmountMode, hasWonAuction]);

  const handleInputChange = (event) => {
    event.persist();
    const name = event.target.name;

    if (name === 'auctionAmount' || name === 'fee' || name === 'expense'
      || name === 'firstAuctionMembersPay' || name === 'amountChoiceSelection') {
      setMissingPayableCalcError("");
      resetDynamicValues();
      setPayableCalculated(false);
      if (name === 'amountChoiceSelection') {
        let expectedState = !getValues('amountChoiceSelection');
        setValue('amountChoiceSelection', expectedState)
        setAuctionAmountMode(expectedState);
      }
    }
  };

  const handleWinningStateChange = (event) => {
    event.persist();

    let hasWon = hasWonAuction;
    setHasWonAuction(!hasWon);
    if (!hasWon) {
      setValue("winnerName", "");
    }
  };

  function resetDynamicValues() {
    payableCalculation['previousWinnersPay'] = 0.0;
    payableCalculation['previousWinnersCount'] = 0;
    payableCalculation['commission'] = 0.0;
    payableCalculation['totalExpense'] = 0.0;
    payableCalculation['remainingMembers'] = 0;
    payableCalculation['remainingMembersPay'] = 0.0;
    payableCalculation['winnerTakes'] = 0.0;
    setPayableCalculation(payableCalculation);
  }

  const calculatePayable = (event) => {
    event.preventDefault();
    setPayableCalculated(false);
    const fee = Number(getValues("fee"));
    const expense = Number(getValues("expense"));
    const auctionAmount = Number(getValues("auctionAmount"));
    const firstAuctionMembersPay = Number(getValues("firstAuctionMembersPay"));
    const perPersonAmount = chit.per_person_amount;

    const previousWinnersCount = currentAuctionCount - 1;
    const remainingMembersCount = chit.total_members - currentAuctionCount;
    payableCalculation['previousWinnersCount'] = previousWinnersCount;
    payableCalculation['remainingMembers'] = remainingMembersCount;

    if (currentAuctionCount !== 1) {
      const previousWinnersPay = fee ? (perPersonAmount - fee) : perPersonAmount;
      payableCalculation['previousWinnersPay'] = Math.round(previousWinnersPay);
    }

    var expenses;
    if (fee && expense) {
      expenses = (fee * (previousWinnersCount)) + expense;
    } else if (!fee && expense) {
      expenses = expense;
    } else if (fee && !expense) {
      expenses = fee * (previousWinnersCount);
    }
    payableCalculation['totalExpense'] = (expenses) ? Math.round(expenses) : 0;

    const totalAmount = chit.total_members * perPersonAmount;
    if (currentAuctionCount !== 1 && auctionAmount) {
      let remainingAmount = totalAmount - auctionAmount;
      let winnerTakes = remainingAmount - perPersonAmount;

      let forMembers = expenses ? (auctionAmount - expenses) : auctionAmount;
      let remainingMembersSave = forMembers / remainingMembersCount;
      let remainingMembersPay = perPersonAmount - remainingMembersSave;

      payableCalculation['commission'] = Math.round(remainingMembersSave);
      payableCalculation['remainingMembersPay'] = Math.round(remainingMembersPay);
      payableCalculation['winnerTakes'] = Math.round(winnerTakes);
    } else if (currentAuctionCount === 1 || !auctionAmountMode) {
      // First auction will not have an auction amount
      // Other auctions too may not have auction amount if user opted-out "amountChoiceSelection"
      let fromPreviousWinners = previousWinnersCount * payableCalculation['previousWinnersPay'];
      let winnerTakes = (firstAuctionMembersPay * remainingMembersCount) + fromPreviousWinners;
      let firstAuctionAmountCalculated = totalAmount - (winnerTakes + perPersonAmount);

      payableCalculation['remainingMembersPay'] = firstAuctionMembersPay;
      payableCalculation['winnerTakes'] = Math.round(winnerTakes);
      setValue('auctionAmount', Number(firstAuctionAmountCalculated));
    } else if (currentAuctionCount === chit.total_members) {
      // Last auction will not have an auction amount
      let remainingAmount = (expenses) ? totalAmount - expenses : totalAmount;
      let winnerTakes = remainingAmount - perPersonAmount;
      payableCalculation['winnerTakes'] = Math.round(winnerTakes);
    }

    setPayableCalculation(payableCalculation);
    setMissingPayableCalcError("");
    setPayableCalculated(true);
  }

  // Handle form submission by creating/updating auction
  function onSubmit(data) {
    if (payableCalculated) {
      return isAddMode
        ? createAuction(data)
        : updateAuction(auctionId, data);
    } else {
      setMissingPayableCalcError(t('text.calculatePayableErrMsg'));
      return false;
    }
  }

  const createAuction = async (input) => {
    let didSucceed = false;
    setAddingAuction(true);
    let lastAuctionDateObject = new Date(chit.next_auction_date);
    const upcomingAuctionDateObject = add(lastAuctionDateObject, { months: chit.auction_interval });
    const upcoming_auction_count = currentAuctionCount + 1;
    let createAuctionInput = {
      _auction_amount: (input.auctionAmount) ? input.auctionAmount : 0,
      _auction_date: input.auctionDate,
      _auction_number: currentAuctionCount,
      _chit_id: chit.id,
      _commission: payableCalculation.commission,
      _expense: input.expense,
      _fee: input.fee,
      _previous_winners_pay: payableCalculation.previousWinnersPay,
      _rem_members_pay: payableCalculation.remainingMembersPay,
      _total_expenses: payableCalculation.totalExpense,
      _upcoming_auction_date: upcomingAuctionDateObject,
      _upcoming_auction_count: upcoming_auction_count,
      _user_id: user.id,
      _winner_name: input.winnerName,
      _winner_takes: payableCalculation.winnerTakes,
      _has_won: hasWonAuction
    };
    // Calls the Postgres function which was created in Supabse console
    const { data, error } = await supabase.rpc('create_auction', createAuctionInput);
    if (error) {
      console.log("Error during auction creation", error.message);
      setAddAuctionError(t('text.dataUpdateErrorGeneric'));
      setAddingAuction(false);
    } else {
      console.log("Created auction and updated respective chit.");
      didSucceed = true;
      setAddingAuction(false);
    }
    if (didSucceed) {
      console.log(`Chit auction saved successfully! Id: ${data}`)
      history.push(`/chit/${chit.id}`)
    }
  }

  const updateAuction = async (id, input) => {
    let updateAuctionInput = {
      _auction_id: id,
      _auction_amount: input.auctionAmount,
      _auction_date: input.auctionDate,
      _expense: input.expense,
      _fee: input.fee,
      _winner_name: input.winnerName,
      _commission: payableCalculation.commission,
      _previous_winners_pay: payableCalculation.previousWinnersPay,
      _rem_members_pay: payableCalculation.remainingMembersPay,
      _total_expenses: payableCalculation.totalExpense,
      _winner_takes: payableCalculation.winnerTakes,
      _has_won: hasWonAuction,
      _auction_count: currentAuctionCount,
      _user_id: user.id,
      _chit_id: chit.id,
    };
    // Calls the Postgres function which was created in Supabse console
    const { data, error } = await supabase.rpc('update_auction', updateAuctionInput);
    if (error) {
      console.log("Error during auction update", error.message);
      setAddAuctionError(t('text.dataUpdateErrorGeneric'));
      setAddingAuction(false);
    } else {
      console.log(`Auction updated successfully! Id: ${data}`);
      history.push(`/chit/${chit.id}`)
    }
  }

  let dateInputProps = {
    placeholder: "Auction Date",
    className: "shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
  }

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'INR',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });
  const totalChitAmount = formatter.format(chit.total_members * chit.per_person_amount);

  return (
    <div className="container flex flex-col mb-4">
      {
        loadingChit || loadingAuction || loadingPrevAuction ?
          <Grid className="mx-auto mt-8" type="Grid" color="#93C5FD" height="32" width="32" /> :
          <div className="sm:w-2/4 w-3/4 mx-auto">
            <h4 className="text-lg font-extrabold mt-4 tracking-normal">
              {
                isAddMode ?
                  t('text.addAuctionTitle', { chitName: `${chit.chit_name} - ${totalChitAmount}` }) :
                  t('text.editAuctionTitle', { chitName: `${chit.chit_name} - ${totalChitAmount}` })
              }
            </h4>
            {
              hasPrevAuction &&
              <div className="bg-blue-300 px-8 pt-3 pb-3 mt-2 rounded-md">
                <p className="text-gray-700 text-base">
                  {t('text.prevAuctionAmount')}: <span className="text-gray-900">{formatter.format(prevAuction.auction_amount)}</span>
                </p>
                <p className="text-gray-700 text-base">
                  {t('text.remMembersPaid')}: <span className="text-gray-900">{formatter.format(prevAuction.rem_members_pay)}</span>
                </p>
                <p className="text-gray-700 text-base">
                  {t('text.prevWinnerTook')}: <span className="text-gray-900">{formatter.format(prevAuction.winner_takes)}</span>
                </p>
              </div>
            }
            <form className="shadow-md rounded-md px-8 pt-6 pb-8 mb-4 bg-blue-300 mt-4" onReset={reset} onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="auction-number">
                  {t('label.auctionNumber')}
                </label>
                <div className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md">
                  {currentAuctionCount}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="total-members">
                  {t('label.totalMembers')}
                </label>
                <div className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md">
                  {chit.total_members}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="auction-date">
                  {t('label.auctionDate')}
                </label>
                <Controller
                  control={control}
                  name="auctionDate"
                  id="auction-date"
                  render={({ field }) => (
                    <Datetime
                      timeFormat={false}
                      closeOnSelect={true}
                      inputProps={dateInputProps}
                      onChange={(date) => field.onChange(date)}
                      value={field.value} />
                  )} />
                <span className="text-red-600 text-s italic">
                  {errors.auctionDate?.message}</span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fee">
                  {t('text.fee')}
                </label>
                <input
                  id="fee"
                  className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="number"
                  placeholder={t('text.fee')}
                  name="fee"
                  {...register("fee")}
                  onChange={(e) => { handleInputChange(e) }} />
                <span className="text-red-600 text-s italic">
                  {errors.fee?.message}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="expense">
                  {t('text.expense')}
                </label>
                <input
                  id="expense"
                  className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="number"
                  placeholder={t('text.expense')}
                  name="expense"
                  {...register("expense")}
                  onChange={(e) => { handleInputChange(e) }} />
                <span className="text-red-600 text-s italic">
                  {errors.expense?.message}
                </span>
              </div>

              {(currentAuctionCount !== 1 && currentAuctionCount < chit.total_members) ?
                <div>
                  {(isAddMode) &&
                    <div className="mb-4 flex items-center">
                      <input
                        className="mr-2 h-5 w-5"
                        type="checkbox"
                        id="amount-choice"
                        name="amountChoiceSelection"
                        {...register("amountChoiceSelection")}
                        onChange={(e) => { handleInputChange(e) }} />
                      <label className='text-gray-700 text-sm font-bold' htmlFor="amount-choice">{t('label.amountChoiceSelection')}</label>
                    </div>
                  }
                  {(auctionAmountMode) &&
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="auction-amount">
                        {t('label.auctionAmount')}
                      </label>
                      <input
                        id="auction-amount"
                        className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                        type="number"
                        placeholder={t('label.auctionAmount')}
                        name="auctionAmount"
                        {...register("auctionAmount")}
                        onChange={(e) => { handleInputChange(e) }} />
                      <span className="text-red-600 text-s italic">
                        {errors.auctionAmount?.message}
                      </span>
                    </div>
                  }
                </div> : ""}

              {(currentAuctionCount === 1 || !auctionAmountMode) ?
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="members-pay">
                    {t('label.membersPay')}
                  </label>
                  <input
                    id="members-pay"
                    className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                    type="number"
                    placeholder={t('label.membersPay')}
                    name="firstAuctionMembersPay"
                    {...register("firstAuctionMembersPay")}
                    onChange={(e) => { handleInputChange(e) }} />
                  <span className="text-red-600 text-s italic">
                    {errors.firstAuctionMembersPay?.message}
                  </span>
                </div> : ""}

              <div className="mb-4">
                <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline" onClick={calculatePayable}>
                  {t('button.calculatePayable')}
                </button>
                {missingPayableCalcError && <div className="text-red-600 text-s italic mt-2">{missingPayableCalcError}</div>}
              </div>

              <hr className="py-2 px-3" />
              {
                !payableCalculated ?
                  <div className="mb-4">{t('text.calculatePayableMessage')}</div> :
                  <div className="mb-4">
                    {
                      (payableCalculation.previousWinnersCount > 0) ?
                        <p className="mb-2">{t('text.previousWinnersPay', { count: payableCalculation.previousWinnersCount, amount: formatter.format(payableCalculation.previousWinnersPay) })}</p> : ""
                    }
                    {
                      (payableCalculation.commission > 0) ?
                        <p className="mb-2">{t('text.commission')}: <b>{formatter.format(payableCalculation.commission)}</b></p> : ""
                    }
                    {
                      (payableCalculation.totalExpense > 0.0) ?
                        <p className="mb-2">{t('text.totalExpenses')}: <b>{formatter.format(payableCalculation.totalExpense)}</b></p> : ""
                    }
                    {
                      (payableCalculation.remainingMembers > 0) ?
                        <p className="mb-2">{t('text.remMembersPay', { count: payableCalculation.remainingMembers, amount: formatter.format(payableCalculation.remainingMembersPay) })}</p> : ""
                    }
                    {/* <p>Winner takes <b>{formatter.format(payableCalculation.winnerTakes)}</b></p> */}
                    <p>{t('text.winnerTakes', { amount: formatter.format(payableCalculation.winnerTakes) })}</p>
                  </div>
              }
              <hr className="py-2 px-3" />

              <div className="mb-4 flex items-center">
                <input
                  className="mr-2 h-5 w-5"
                  type="checkbox"
                  id="has-won-choice"
                  name="hasWonSelection"
                  {...register("hasWonSelection")}
                  onChange={(e) => { handleWinningStateChange(e) }} />
                <label className='text-gray-700 text-sm font-bold' htmlFor="has-won-choice">
                  {t('label.hasWonSelection')}
                </label>
              </div>

              {(!hasWonAuction) &&
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="winner-name">
                    {t('label.winnerName')}
                  </label>
                  <input
                    id="winner-name"
                    className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                    type="text"
                    placeholder={t('label.winnerName')}
                    name="winnerName"
                    {...register("winnerName")} />
                </div>
              }

              {addAuctionError && <div className="text-red-600 text-s italic mb-2">{addAuctionError}</div>}
              <div className="flex">
                <button disabled={isSubmitting} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4" type="submit">
                  {t('button.saveAuction')}
                </button>
                {(addingAuction || isSubmitting) && <span className="my-auto"><Grid type="Grid" color="#F9FAFB" height="28" width="28" /></span>}
              </div>
            </form>
          </div>
      }
    </div>
  );
}
